import { useState, useEffect } from 'react';
import { Settings, Bell, Shield, Database, Globe, Save, CheckCircle, AlertCircle, X, LogOut, Monitor, Smartphone, Loader2 } from 'lucide-react';
import { apiFetch } from '../utils/api';

interface Session {
  id: string;
  createdAt: string;
  expiresAt: string;
  userAgent?: string;
  ipAddress?: string;
  isCurrent?: boolean;
}

export default function SettingsPage() {
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 'security') {
      fetchSessions();
    }
  }, [activeTab]);

  async function fetchSessions() {
    setSessionsLoading(true);
    try {
      const response = await apiFetch('/api/auth/sessions');
      const data = await response.json();
      if (response.ok) {
        setSessions(data.sessions || []);
      }
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
    } finally {
      setSessionsLoading(false);
    }
  }

  async function handleLogoutSession(sessionId: string) {
    try {
      const response = await apiFetch(`/api/auth/sessions/${sessionId}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        setSuccess('セッションをログアウトしました');
        fetchSessions();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError('セッションの削除に失敗しました');
      setTimeout(() => setError(''), 3000);
    }
  }

  async function handleLogoutAllSessions() {
    if (!confirm('他のすべてのデバイスからログアウトしますか？')) return;
    try {
      const response = await apiFetch('/api/auth/sessions', {
        method: 'DELETE',
      });
      if (response.ok) {
        setSuccess('他のすべてのセッションをログアウトしました');
        fetchSessions();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError('セッションの削除に失敗しました');
      setTimeout(() => setError(''), 3000);
    }
  }

  async function handleExportAllData() {
    setExportLoading(true);
    try {
      // 複数のデータを並行して取得
      const [ordersRes, inventoryRes, productsRes] = await Promise.all([
        apiFetch('/api/orders?limit=10000'),
        apiFetch('/api/inventory?limit=10000'),
        apiFetch('/api/products?limit=10000'),
      ]);

      const ordersData = await ordersRes.json();
      const inventoryData = await inventoryRes.json();
      const productsData = await productsRes.json();

      // JSONファイルとしてエクスポート
      const exportData = {
        exportedAt: new Date().toISOString(),
        orders: ordersData.orders || [],
        inventory: inventoryData.inventory || [],
        products: productsData.products || [],
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `zaiko-export-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(link.href);

      setSuccess('データをエクスポートしました');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('データのエクスポートに失敗しました');
      setTimeout(() => setError(''), 3000);
    } finally {
      setExportLoading(false);
    }
  }

  const tabs = [
    { id: 'general', name: '一般設定', icon: Settings },
    { id: 'notification', name: '通知設定', icon: Bell },
    { id: 'security', name: 'セキュリティ', icon: Shield },
    { id: 'api', name: 'API設定', icon: Globe },
    { id: 'data', name: 'データ管理', icon: Database },
  ];

  function handleSave(section: string) {
    setLoading(true);
    // ローカル設定として保存（将来的にはAPI経由でサーバーに保存）
    setTimeout(() => {
      setLoading(false);
      // LocalStorageに保存する（一時的な対応）
      try {
        const currentSettings = JSON.parse(localStorage.getItem('appSettings') || '{}');
        localStorage.setItem('appSettings', JSON.stringify({
          ...currentSettings,
          [section]: { savedAt: new Date().toISOString() }
        }));
        setSuccess(`${section}を保存しました`);
      } catch {
        setSuccess(`${section}の変更を確認しました`);
      }
      setTimeout(() => setSuccess(''), 3000);
    }, 300);
  }

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
          <Settings size={28} />
          設定
        </h1>
        <p className="mt-1" style={{ color: 'var(--text-secondary)' }}>
          システム全体の設定を管理
        </p>
      </div>

      {/* メッセージ */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle size={18} />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X size={16} /></button>
        </div>
      )}
      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
          <CheckCircle size={18} />
          <span>{success}</span>
          <button onClick={() => setSuccess('')} className="ml-auto"><X size={16} /></button>
        </div>
      )}

      <div className="flex gap-6">
        {/* サイドバー */}
        <div className="w-48 flex-shrink-0">
          <nav className="space-y-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                    activeTab === tab.id ? 'bg-blue-50 text-blue-600' : 'hover:bg-gray-100'
                  }`}
                  style={activeTab !== tab.id ? { color: 'var(--text-primary)' } : {}}
                >
                  <Icon size={18} />
                  <span className="text-sm">{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* コンテンツ */}
        <div className="flex-1 card p-6">
          {activeTab === 'general' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>一般設定</h2>
              
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>会社名</label>
                <input
                  type="text"
                  defaultValue="サンプル株式会社"
                  className="w-full px-4 py-2 border rounded-lg"
                  style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>タイムゾーン</label>
                <select
                  defaultValue="Asia/Tokyo"
                  className="w-full px-4 py-2 border rounded-lg"
                  style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                >
                  <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                  <option value="UTC">UTC</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>日付フォーマット</label>
                <select
                  defaultValue="yyyy/MM/dd"
                  className="w-full px-4 py-2 border rounded-lg"
                  style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                >
                  <option value="yyyy/MM/dd">2024/01/01</option>
                  <option value="yyyy-MM-dd">2024-01-01</option>
                  <option value="MM/dd/yyyy">01/01/2024</option>
                </select>
              </div>

              <button
                onClick={() => handleSave('設定')}
                disabled={loading}
                className="btn-primary flex items-center gap-2"
              >
                <Save size={18} />
                {loading ? '保存中...' : '保存'}
              </button>
            </div>
          )}

          {activeTab === 'notification' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>通知設定</h2>
              
              <div className="space-y-4">
                <label className="flex items-center justify-between p-4 rounded-lg" style={{ backgroundColor: 'var(--light-gray-primary)' }}>
                  <div>
                    <p className="font-medium" style={{ color: 'var(--text-primary)' }}>新規受注通知</p>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>新しい受注があった際に通知</p>
                  </div>
                  <input type="checkbox" defaultChecked className="w-5 h-5 rounded" />
                </label>

                <label className="flex items-center justify-between p-4 rounded-lg" style={{ backgroundColor: 'var(--light-gray-primary)' }}>
                  <div>
                    <p className="font-medium" style={{ color: 'var(--text-primary)' }}>在庫アラート通知</p>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>在庫が安全在庫を下回った際に通知</p>
                  </div>
                  <input type="checkbox" defaultChecked className="w-5 h-5 rounded" />
                </label>

                <label className="flex items-center justify-between p-4 rounded-lg" style={{ backgroundColor: 'var(--light-gray-primary)' }}>
                  <div>
                    <p className="font-medium" style={{ color: 'var(--text-primary)' }}>同期エラー通知</p>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>同期処理でエラーが発生した際に通知</p>
                  </div>
                  <input type="checkbox" defaultChecked className="w-5 h-5 rounded" />
                </label>

                <label className="flex items-center justify-between p-4 rounded-lg" style={{ backgroundColor: 'var(--light-gray-primary)' }}>
                  <div>
                    <p className="font-medium" style={{ color: 'var(--text-primary)' }}>メール通知</p>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>重要な通知をメールで受信</p>
                  </div>
                  <input type="checkbox" className="w-5 h-5 rounded" />
                </label>
              </div>

              <button
                onClick={() => handleSave('通知設定')}
                disabled={loading}
                className="btn-primary flex items-center gap-2"
              >
                <Save size={18} />
                {loading ? '保存中...' : '保存'}
              </button>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>セキュリティ設定</h2>
              
              <div className="p-4 rounded-lg border" style={{ borderColor: 'var(--border-color)' }}>
                <h3 className="font-medium mb-2" style={{ color: 'var(--text-primary)' }}>二要素認証</h3>
                <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
                  アカウントのセキュリティを強化するため、二要素認証を有効にすることをお勧めします。
                </p>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm flex items-center gap-2">
                  <AlertCircle size={16} />
                  この機能は今後のアップデートで追加予定です
                </div>
              </div>

              <div className="p-4 rounded-lg border" style={{ borderColor: 'var(--border-color)' }}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-medium" style={{ color: 'var(--text-primary)' }}>セッション管理</h3>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      アクティブなログインセッションを管理
                    </p>
                  </div>
                  {sessions.length > 1 && (
                    <button
                      onClick={handleLogoutAllSessions}
                      className="text-sm text-red-600 hover:text-red-800 flex items-center gap-1"
                    >
                      <LogOut size={14} />
                      他をすべてログアウト
                    </button>
                  )}
                </div>

                {sessionsLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 size={20} className="animate-spin" style={{ color: 'var(--text-secondary)' }} />
                  </div>
                ) : sessions.length === 0 ? (
                  <p className="text-sm text-center py-4" style={{ color: 'var(--text-secondary)' }}>
                    セッション情報を取得できませんでした
                  </p>
                ) : (
                  <div className="space-y-2">
                    {sessions.map((session, index) => (
                      <div
                        key={session.id}
                        className="flex items-center justify-between p-3 rounded-lg"
                        style={{ backgroundColor: 'var(--light-gray-primary)' }}
                      >
                        <div className="flex items-center gap-3">
                          {session.userAgent?.includes('Mobile') ? (
                            <Smartphone size={20} style={{ color: 'var(--text-secondary)' }} />
                          ) : (
                            <Monitor size={20} style={{ color: 'var(--text-secondary)' }} />
                          )}
                          <div>
                            <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                              {index === 0 ? 'このデバイス' : `セッション ${index + 1}`}
                              {session.isCurrent && (
                                <span className="ml-2 px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded">現在</span>
                              )}
                            </p>
                            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                              ログイン: {new Date(session.createdAt).toLocaleString('ja-JP')}
                            </p>
                          </div>
                        </div>
                        {index !== 0 && (
                          <button
                            onClick={() => handleLogoutSession(session.id)}
                            className="text-sm text-red-600 hover:text-red-800"
                          >
                            ログアウト
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="p-4 rounded-lg border" style={{ borderColor: 'var(--border-color)' }}>
                <h3 className="font-medium mb-2" style={{ color: 'var(--text-primary)' }}>ログイン履歴</h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  最終ログイン: {new Date().toLocaleString('ja-JP')}
                </p>
              </div>
            </div>
          )}

          {activeTab === 'api' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>API設定</h2>
              
              <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--light-gray-primary)' }}>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <AlertCircle size={16} className="inline mr-1" />
                  API設定は店舗連携ページで各店舗ごとに管理します。
                </p>
              </div>

              <div>
                <h3 className="font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Webhook URL</h3>
                <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
                  外部サービスからの通知を受け取るためのURL
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value="https://zaiko-adjust-app.valleyintest.workers.dev/api/webhook"
                    className="flex-1 px-4 py-2 border rounded-lg font-mono text-sm bg-gray-100"
                    style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText('https://zaiko-adjust-app.valleyintest.workers.dev/api/webhook');
                      setSuccess('URLをコピーしました');
                      setTimeout(() => setSuccess(''), 2000);
                    }}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                    style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                  >
                    コピー
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'data' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>データ管理</h2>
              
              <div className="p-4 rounded-lg border" style={{ borderColor: 'var(--border-color)' }}>
                <h3 className="font-medium mb-2" style={{ color: 'var(--text-primary)' }}>データエクスポート</h3>
                <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
                  システム内の全データ（受注、在庫、商品）をJSON形式で一括エクスポートします。
                </p>
                <button
                  onClick={handleExportAllData}
                  disabled={exportLoading}
                  className="btn-primary flex items-center gap-2"
                >
                  {exportLoading ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Database size={18} />
                  )}
                  {exportLoading ? 'エクスポート中...' : '全データをエクスポート'}
                </button>
                <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>
                  ※ 各ページのCSVエクスポート機能も引き続きご利用いただけます
                </p>
              </div>

              <div className="p-4 rounded-lg border border-red-200">
                <h3 className="font-medium mb-2 text-red-600">データ削除</h3>
                <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
                  アカウントと全データを完全に削除します。この操作は取り消せません。
                </p>
                <button
                  onClick={() => {
                    if (confirm('本当にすべてのデータを削除しますか？この操作は取り消せません。')) {
                      if (confirm('最終確認: 本当に削除してよろしいですか？')) {
                        setError('データ削除機能は管理者にお問い合わせください');
                        setTimeout(() => setError(''), 5000);
                      }
                    }
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  全データを削除
                </button>
                <p className="text-xs mt-2 text-red-500">
                  ⚠ この操作は取り消すことができません
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
